// Today's insights: written by the AI once a day from the trends (or the
// strongest rule-based ones), each saying what it's based on.

import { CheckCircle2, Eye, Lightbulb, RefreshCw, Sparkles } from 'lucide-react';
import { useDailyInsights } from '../../data/live.ts';
import type { DailyInsight } from '../../domain/insights.ts';
import { formatTime } from '../../domain/time.ts';
import { navigate } from '../../app/router.ts';
import { Card } from '../../ui/primitives.tsx';
import { FromTag } from '../../ui/Sources.tsx';

const KIND = {
  win: { icon: CheckCircle2, tone: 'good', label: 'Win' },
  tip: { icon: Lightbulb, tone: 'info', label: 'Try this' },
  watch: { icon: Eye, tone: 'warn', label: 'Keep an eye' },
} as const;

function Item({ i }: { i: DailyInsight }) {
  const k = KIND[i.kind];
  const body = (
    <>
      <span className={`insight-icon tone-${k.tone}`} aria-hidden>
        <k.icon />
      </span>
      <span className="insight-text">
        <span className="insight-kind">{k.label}</span>
        <strong>{i.title}</strong>
        <span>{i.detail}</span>
        <span className="insight-based">
          <FromTag from="Based on" /> {i.basedOn}
        </span>
      </span>
    </>
  );
  return i.route ? (
    <button type="button" className="insight" onClick={() => navigate(i.route!)}>
      {body}
    </button>
  ) : (
    <div className="insight">{body}</div>
  );
}

/** The Insights page's hero. */
export function DailyInsightsCard() {
  const { insights: d, loading, refresh } = useDailyInsights();
  return (
    <Card className="daily-insights">
      <header className="daily-head">
        <span className="eyebrow">
          <Sparkles aria-hidden className="inline-icon" /> Today’s insights
        </span>
        <button type="button" className="link small" onClick={refresh} disabled={loading} aria-label="Refresh today’s insights">
          <RefreshCw aria-hidden className={`inline-icon${loading ? ' spin' : ''}`} /> {loading ? 'Thinking…' : 'Refresh'}
        </button>
      </header>
      <h2 className="daily-headline">{d.headline}</h2>
      {d.insights.length > 0 && (
        <div className="insight-list">
          {d.insights.map((i) => (
            <Item key={i.title} i={i} />
          ))}
        </div>
      )}
      <p className="muted small daily-foot">
        {d.via === 'ai' ? `Written by ${d.engine === 'local' ? 'your AI at home' : 'AI'} from his logs${d.at ? ` at ${formatTime(d.at)}` : ''} — check anything important with your vet.` : 'From the patterns in his logs. With AI switched on, a fresh write-up arrives each morning.'}
      </p>
    </Card>
  );
}

/** Today's side column: the headline and the first insight (just the headline on the kitchen tablet, where space is short). */
export function InsightOfTheDay({ compact }: { compact?: boolean }) {
  const { insights: d } = useDailyInsights();
  if (!d.insights.length) return null;
  const first = d.insights[0];
  const k = KIND[first.kind];
  return (
    <Card className="insight-day" onClick={() => navigate('/insights')}>
      <span className="eyebrow">
        <Sparkles aria-hidden className="inline-icon" /> Insight of the day
      </span>
      <p className="insight-day-head">{d.headline}</p>
      {/* The headline often already names the first insight — then its "what to do" is the useful line. */}
      {!compact && (
      <p className="small insight-day-item">
        <k.icon aria-hidden className={`inline-icon tone-${k.tone}`} /> {d.headline.includes(first.title) ? first.detail : first.title}
      </p>
      )}
      <span className="link small">All {d.insights.length} insights →</span>
    </Card>
  );
}
